import React from 'react';
import delve from 'dlv';
import useCollapse from 'react-collapsed';

import KeyValuePair from 'components/base/KeyValuePair/KeyValuePair';
import ArtistLinks from 'components/Header/EntityMetadata/ArtistLinks';

import formatNumber from 'helpers/formatNumber';
import formatDate from 'helpers/formatDate';
import pluralize from 'helpers/pluralize';
import colors from 'styles/themes/colors';
import getEntityUrl from 'url-constructors/getEntityUrl';
import { apiTypeToEntityType } from 'helpers/mapEntityTypes';
import {
  DefaultMetaData,
  Date,
  ExpandableMetaData,
  ToggleDetailsButton,
} from '../EntityMetadata';
import shouldRenderValue from 'helpers/shouldRenderValue';

/** @component */
const Project: React.FC<any> = ({ entity, artists, metadata }) => {
  const { grouped_track_count, grouped_album_count, type, id, releaseDate } =
    entity;

  const [track] = metadata?.tracksExcerpt ?? [];
  const [release] = metadata?.albumsExcerpt ?? [];

  const animation = { transitionDuration: '300ms' };
  const { getCollapseProps, getToggleProps, isOpen } = useCollapse({
    collapseStyles: animation,
    expandStyles: animation,
  });

  return (
    <>
      <DefaultMetaData>
        <ArtistLinks artists={artists} />

        {shouldRenderValue(releaseDate) && (
          <Date>{formatDate(releaseDate, 'day-short')}</Date>
        )}
        <ToggleDetailsButton
          {...getToggleProps()}
          data-pw="toggle-entity-metadata-details"
        >
          {isOpen ? 'Hide details' : 'Show details'}
        </ToggleDetailsButton>
      </DefaultMetaData>
      <ExpandableMetaData {...getCollapseProps()}>
        {!grouped_track_count && (
          <KeyValuePair dataColor={colors.DeepBlue} label="Tracks" value="-" />
        )}
        {grouped_track_count === 1 && track && (
          <KeyValuePair
            dataColor={colors.DeepBlue}
            label="Track"
            value={delve(track, 'displayName', null)}
            to={getEntityUrl({
              entityType: track.type,
              entityId: track.id,
              topic: 'overview/view',
            })}
            preserveSearch={true}
            withChevron
          />
        )}
        {grouped_track_count > 1 && (
          <KeyValuePair
            dataColor={colors.DeepBlue}
            label="Tracks"
            value={`${formatNumber(grouped_track_count)} ${pluralize(
              'track',
              grouped_track_count
            )}`}
            to={getEntityUrl({
              entityType: type,
              entityId: id,
              topic: 'overview/view',
            })}
            preserveSearch={true}
          />
        )}

        {!grouped_album_count && (
          <KeyValuePair dataColor={colors.DeepBlue} label="Release" value="-" />
        )}
        {grouped_album_count === 1 && release && (
          <KeyValuePair
            dataColor={colors.DeepBlue}
            label="Release"
            value={delve(release, 'displayName', null)}
            to={getEntityUrl({
              entityType: apiTypeToEntityType(release.type),
              entityId: release.id,
              topic: 'overview/view',
            })}
            preserveSearch={true}
            withChevron
          />
        )}
        {grouped_album_count > 1 && (
          <KeyValuePair
            dataColor={colors.DeepBlue}
            label="Releases"
            value={`${formatNumber(grouped_album_count)} ${pluralize(
              'release',
              grouped_album_count
            )}`}
            to={getEntityUrl({
              entityType: type,
              entityId: id,
              topic: 'overview/view',
            })}
            preserveSearch={true}
          />
        )}
      </ExpandableMetaData>
    </>
  );
};

export default Project;
